import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react';

type ToastVariant = 'success' | 'danger' | 'warning' | 'default';
interface Toast { id: string; message: string; variant: ToastVariant; }

interface ToastApi {
  show: (message: string, variant?: ToastVariant) => void;
  success: (message: string) => void;
  error: (message: string) => void;
  warning: (message: string) => void;
}

const Ctx = createContext<ToastApi | null>(null);

const ICONS: Record<ToastVariant, string> = {
  success: '✓',
  danger: '!',
  warning: '!',
  default: 'i',
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const show = useCallback((message: string, variant: ToastVariant = 'default') => {
    const id = (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : Math.random().toString(36).slice(2);
    setToasts((prev) => [...prev, { id, message, variant }]);
  }, []);

  const dismiss = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const api: ToastApi = {
    show,
    success: (m) => show(m, 'success'),
    error: (m) => show(m, 'danger'),
    warning: (m) => show(m, 'warning'),
  };

  return (
    <Ctx.Provider value={api}>
      {children}
      <div className="fixed bottom-5 right-5 flex flex-col gap-2 z-[2000] pointer-events-none">
        {toasts.map((t) => (
          <ToastItem key={t.id} toast={t} onDismiss={() => dismiss(t.id)} />
        ))}
      </div>
    </Ctx.Provider>
  );
}

function ToastItem({ toast, onDismiss }: { toast: Toast; onDismiss: () => void }) {
  const [paused, setPaused] = useState(false);
  const [enter, setEnter] = useState(false);

  useEffect(() => {
    requestAnimationFrame(() => setEnter(true));
  }, []);

  useEffect(() => {
    if (paused) return;
    const t = setTimeout(onDismiss, 4500);
    return () => clearTimeout(t);
  }, [paused, onDismiss]);

  const variantClasses: Record<ToastVariant, string> = {
    success: 'bg-success text-white',
    danger: 'bg-danger text-white',
    warning: 'bg-accent text-white',
    default: 'bg-primary text-white',
  };

  return (
    <div
      className={`pointer-events-auto flex items-start gap-3 pl-3 pr-2 py-3 rounded-lg shadow-lg max-w-md min-w-[260px] transition-all duration-200 ${
        variantClasses[toast.variant]
      } ${enter ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-3'}`}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      role="alert"
    >
      <span className="flex-shrink-0 w-6 h-6 rounded-full bg-white/20 flex items-center justify-center text-sm font-bold leading-none">
        {ICONS[toast.variant]}
      </span>
      <span className="text-sm font-medium leading-snug pt-0.5 flex-1">{toast.message}</span>
      <button
        type="button"
        onClick={onDismiss}
        className="flex-shrink-0 w-6 h-6 rounded-full hover:bg-white/15 flex items-center justify-center text-base leading-none opacity-70 hover:opacity-100"
        aria-label="Sluiten"
      >
        ×
      </button>
    </div>
  );
}

export function useToast(): ToastApi {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useToast must be inside ToastProvider');
  return ctx;
}
